import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { House, CalendarBlank, Users, ChartBar, Gear, SignOut, GraduationCap, FilePdf, Keyboard, ShieldCheck, Chalkboard } from "@phosphor-icons/react";
import useKeyboardShortcuts from "@/lib/useKeyboardShortcuts";
import ShortcutsDialog from "@/components/ShortcutsDialog";
import OnboardingWizard from "@/components/OnboardingWizard";
import AssistantWidget from "@/components/AssistantWidget";
import ThemeToggle from "@/components/ThemeToggle";

const navItems = [
  { to: "/", label: "Panel", icon: House, end: true, testid: "nav-dashboard" },
  { to: "/takvim", label: "Takvim", icon: CalendarBlank, testid: "nav-calendar" },
  { to: "/ogrenciler", label: "Öğrenciler", icon: Users, testid: "nav-students" },
  { to: "/ogretmenler", label: "Öğretmenler", icon: Chalkboard, testid: "nav-teachers" },
  { to: "/muhasebe", label: "Muhasebe", icon: ChartBar, testid: "nav-accounting" },
  { to: "/raporlar", label: "Rapor", icon: FilePdf, testid: "nav-reports" },
  { to: "/ayarlar", label: "Ayarlar", icon: Gear, testid: "nav-settings" },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  const isAdmin = user?.role === "admin";
  const planStatus = user?.plan?.status;
  const allNavItems = [
    ...navItems,
    ...(isAdmin ? [{ to: "/admin", label: "Yönetici", icon: ShieldCheck, testid: "nav-admin" }] : []),
  ];

  useKeyboardShortcuts({
    onNewLesson: () => {
      window.dispatchEvent(new CustomEvent("tutorpro:newLesson"));
    },
    onShowHelp: () => setShortcutsOpen((v) => !v),
  });

  return (
    <div className="min-h-screen flex bg-[#FAF9F6]">
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-64 border-r border-border bg-white/60 backdrop-blur-sm">
        <div className="px-5 py-5 flex items-center gap-2 border-b border-border">
          <div className="w-9 h-9 rounded-md bg-[#3A5A40] flex items-center justify-center text-white">
            <GraduationCap size={20} weight="duotone" />
          </div>
          <div>
            <div className="font-display font-bold text-base leading-none">EduFin</div>
            <div className="text-xs text-muted-foreground mt-0.5">Özel ders paneli</div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {allNavItems.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={it.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all duration-200 ${
                  isActive
                    ? "bg-[#3A5A40] text-white shadow-sm"
                    : "text-foreground hover:bg-muted"
                }`
              }
            >
              <it.icon size={18} weight="duotone" />
              <span className="font-medium">{it.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border">
          <div className="text-xs text-muted-foreground mb-2 px-2">
            <div className="truncate font-medium text-foreground" data-testid="sidebar-user-name">{user?.name}</div>
            <div className="truncate">{user?.email}</div>
            {planStatus && planStatus !== "admin" && (
              <div className="mt-1.5" data-testid="sidebar-plan-badge">
                <PlanBadge plan={user?.plan} />
              </div>
            )}
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start gap-2 text-muted-foreground hover:text-foreground"
            onClick={() => setShortcutsOpen(true)}
            data-testid="sidebar-shortcuts-button"
            title="Klavye kısayolları ( ? )"
          >
            <Keyboard size={18} weight="duotone" /> Kısayollar
          </Button>
          <div className="px-1">
            <ThemeToggle className="w-full justify-between" />
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start gap-2 text-muted-foreground hover:text-foreground"
            onClick={async () => { await logout(); nav("/giris"); }}
            data-testid="sidebar-logout-button"
          >
            <SignOut size={18} weight="duotone" /> Çıkış yap
          </Button>
        </div>
      </aside>

      {/* Mobile topbar */}
      <div className="md:hidden w-full fixed top-0 inset-x-0 z-30 glass-header border-b border-border">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2 text-[#3A5A40]">
            <GraduationCap size={22} weight="duotone" />
            <span className="font-display font-bold">EduFin</span>
          </div>
          <div className="flex items-center gap-1">
            <ThemeToggle compact />
            <Button variant="ghost" size="sm" onClick={async () => { await logout(); nav("/giris"); }} data-testid="mobile-logout-button">
              <SignOut size={18} />
            </Button>
          </div>
        </div>
        <nav className="flex gap-1 px-2 pb-2 overflow-x-auto hide-scrollbar">
          {allNavItems.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs whitespace-nowrap border ${
                  isActive ? "bg-[#3A5A40] text-white border-[#3A5A40]" : "border-border text-foreground bg-white"
                }`
              }
            >
              <it.icon size={14} weight="duotone" /> {it.label}
            </NavLink>
          ))}
        </nav>
      </div>

      {/* Main */}
      <main className="flex-1 pt-24 md:pt-0">
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-6 md:py-10 animate-fade-in">
          <Outlet />
        </div>
      </main>

      <ShortcutsDialog open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
      <OnboardingWizard />
      <AssistantWidget />
    </div>
  );
}

function PlanBadge({ plan }) {
  if (!plan) return null;
  const now = Date.now();
  const trialEnd = plan.trial_ends_at ? new Date(plan.trial_ends_at).getTime() : null;
  const subEnd = plan.subscription_ends_at ? new Date(plan.subscription_ends_at).getTime() : null;

  if (plan.status === "trial" && trialEnd) {
    const days = Math.max(0, Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24)));
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-[#FFF3E0] text-[#E65100] border border-[#FFCC80]">
        Deneme · {days} gün kaldı
      </span>
    );
  }
  if (plan.status === "active" && subEnd) {
    const d = new Date(subEnd);
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-[#E8F5E9] text-[#2E7D32] border border-[#A5D6A7]">
        Aktif · {d.toLocaleDateString("tr-TR")}
      </span>
    );
  }
  if (plan.status === "blocked") {
    return <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-[#3E3E3E] text-white">Engelli</span>;
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-[#FDECEA] text-[#C62828] border border-[#EF9A9A]">
      Süresi bitti
    </span>
  );
}
